// Story 1.18 + legal action item 12 / lawyer-review item 7 — the held-funds
// view behind the refund surface.
//
// THE DEFECT THIS EXISTS FOR. `compute_refundable_amount` caps every row at
//
//     refundable = min(FIFO remaining on the top-up, AVAILABLE balance)
//
// and money committed to a live offer has already left `balance_cents` for
// `escrow_cents`. So a user with €50 sitting in a bid gets an EMPTY
// /wallet/refundable-topups list — while the 14-day PSD2 window keeps
// running and offers never auto-expire (ToS 7(8)). Rendering that empty list
// as "nothing to refund" silently runs out a statutory right, which is the
// exact opposite of the visible refund-to-source path counsel required.
//
// Two signals build the notice:
//   * the holds  — the caller's own ACTIVE offers, both item kinds
//     (GET /api/v1/marketplace/me/{share,sealed-dossier}-offers).
//   * the clock  — the `window_open` block on /wallet/refundable-topups; see
//     "Window clock" below.

import { useMemo } from "react";

import { useMyOffers } from "../marketplace/useMarketplaceOffers";
import {
  useRefundEligibleList,
  type RefundableTopupItem,
} from "./useRefundEligibleList";

/** Mirrors `wallet_service::REFUND_WINDOW_DAYS`. */
export const REFUND_WINDOW_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

// ---- Live holds ---------------------------------------------------------

export type HeldOfferFunds = {
  /** Total still held by the caller's own live offers. */
  heldCents: number;
  /** How many live offers the money is spread across. */
  offerCount: number;
  isPending: boolean;
  isError: boolean;
};

/**
 * Sum of the caller's ACTIVE offers across both item kinds — i.e. the money
 * that is theirs, is not refundable, and that no screen would otherwise
 * mention on the refund path.
 */
export function useHeldOfferFunds(): HeldOfferFunds {
  const share = useMyOffers("share");
  const sealed = useMyOffers("sealed_dossier");

  const live = [...(share.data ?? []), ...(sealed.data ?? [])].filter(
    (offer) => offer.status === "active",
  );

  return {
    heldCents: live.reduce((sum, offer) => sum + offer.amount_cents, 0),
    offerCount: live.length,
    isPending: share.isPending || sealed.isPending,
    isError: share.isError || sealed.isError,
  };
}

// ---- Window clock -------------------------------------------------------
//
// The clock answers ONE question: is any top-up still inside its 14-day
// window, and when does the earliest one close. The notice renders a
// consumer-facing NEGATIVE off that answer ("none of your top-ups is still in
// window, releasing this money won't make it refundable"), so the negative has
// to be PROVEN, never inferred from an absence.
//
// Story 6.14 AC14 makes that answer server-authoritative. The refundable-topups
// response now carries a `window_open` block the server computes over the FULL
// in-window top-up set IGNORING the balance cap — so it is correct even when a
// live offer hold has zeroed every `items` row, which is the exact case the
// old client-side `/wallet/history` walk existed to cover. One source now:
// `useRefundEligibleList`, which the page already fetches (React Query dedupes
// the shared key), so the clock costs zero extra requests. `proven` is
// therefore `true` whenever the response has arrived and `false` only while
// pending or errored; there is no absence left to misread.

export type OpenRefundWindow = {
  ledgerEntryId: string;
  toppedUpAt: string;
  amountCents: number;
  /** Whole days left before this top-up leaves the 14-day window. */
  daysRemaining: number;
  /** Instant the window shuts — `toppedUpAt` + 14 days. */
  closesAt: Date;
};

/**
 * Windows straight off the server's own refundable-topups items.
 *
 * `days_remaining` is the server's figure (`compute_refundable_amount`), so
 * this needs no client-side ageing and no history at all. Exported for tests.
 */
export function windowsFromRefundableTopups(
  items: RefundableTopupItem[],
): OpenRefundWindow[] {
  const windows: OpenRefundWindow[] = [];
  for (const item of items) {
    const toppedUpAt = new Date(item.topped_up_at);
    if (Number.isNaN(toppedUpAt.getTime())) continue;
    if (item.days_remaining <= 0) continue;
    windows.push({
      ledgerEntryId: item.ledger_entry_id,
      toppedUpAt: item.topped_up_at,
      amountCents: item.original_amount_cents,
      daysRemaining: item.days_remaining,
      closesAt: new Date(toppedUpAt.getTime() + REFUND_WINDOW_DAYS * DAY_MS),
    });
  }
  return windows.sort((a, b) => a.daysRemaining - b.daysRemaining);
}

export type RefundWindowClock = {
  /** In-window top-ups, soonest to close first. */
  windows: OpenRefundWindow[];
  /**
   * True once the server has answered. `window_open` is computed server-side
   * over the FULL in-window set ignoring the balance cap, so a present response
   * is an authoritative answer — including the negative "no window is open".
   * False only while the request is pending or errored, when the surface stays
   * silent rather than assert a negative it cannot back.
   */
  proven: boolean;
  isPending: boolean;
  isError: boolean;
  /** Which source answered. Exposed for tests + debugging. */
  source: "refundable-topups" | "none";
};

export type RefundWindowClockOptions = {
  /**
   * Skip reading the list. Callers that already know they will render nothing
   * pass `false` to get a silent clock. With the history walk gone this only
   * gates whether we bother depending on the shared query.
   */
  enabled?: boolean;
};

export function useOpenRefundWindows(
  options: RefundWindowClockOptions = {},
): RefundWindowClock {
  const { enabled = true } = options;

  // Same query key as the page's own list — React Query dedupes, so this is
  // free wherever the refund surface already asked the question.
  const eligible = useRefundEligibleList();

  // The clock is only "is any window open + soonest to close". The server's
  // `window_open` answers both over the full in-window set, so one synthesized
  // window for the earliest-closing top-up is all the notice needs. The
  // per-row refundable items are rendered separately by <RefundEligibleList>
  // from `eligible.data.items`.
  const windows = useMemo<OpenRefundWindow[]>(() => {
    if (!eligible.data) return [];
    const wo = eligible.data.window_open;
    if (!wo.any_open) return [];
    return [
      {
        ledgerEntryId: "",
        toppedUpAt: wo.earliest_closes_at ?? "",
        amountCents: 0,
        daysRemaining: wo.days_remaining,
        closesAt: wo.earliest_closes_at
          ? new Date(wo.earliest_closes_at)
          : new Date(),
      },
    ];
  }, [eligible.data]);

  if (!enabled) {
    return {
      windows: [],
      proven: false,
      isPending: false,
      isError: false,
      source: "none",
    };
  }

  if (eligible.data) {
    return {
      windows,
      proven: true,
      isPending: false,
      isError: false,
      source: "refundable-topups",
    };
  }

  return {
    windows: [],
    proven: false,
    isPending: eligible.isPending,
    isError: eligible.isError,
    source: "none",
  };
}
